import { motion } from "framer-motion";
import {
  Activity, BarChart3, LayoutDashboard, Wallet, Users,
  ArrowUpRight, ArrowDownRight, Check, Clock, TrendingUp,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { TASFLOW_I18N } from "../i18n/tasflow";

const rise = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

function StoryRow({ id, eyebrow, icon: Icon, title, accent, desc, points, reverse, children, testid }) {
  return (
    <section id={id} className="py-20 sm:py-28 scroll-mt-24" data-testid={testid}>
      <div className="container-x">
        <div className={`grid items-center gap-12 lg:grid-cols-2 ${reverse ? "lg:[direction:rtl]" : ""}`}>
          <motion.div {...rise} transition={{ duration: 0.6 }} className="lg:[direction:ltr]">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
              <Icon className="h-3 w-3" /> {eyebrow}
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-[2.7rem] font-extrabold leading-[1.08] tracking-tight text-white">
              {title} <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">{accent}</span>
            </h2>
            <p className="mt-5 max-w-xl text-white/60 leading-relaxed">{desc}</p>
            <ul className="mt-7 space-y-3">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-white/80">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#F97316]/15 border border-[#F97316]/30">
                    <Check className="h-3 w-3 text-[#FB923C]" strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:[direction:ltr]"
          >
            {children}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Panel({ children }) {
  return (
    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-6 backdrop-blur-xl shadow-2xl shadow-black/40">
      <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br from-[#F97316]/10 to-transparent opacity-60" />
      <div className="relative">{children}</div>
    </div>
  );
}

const PULSE_UP = [true, false, false, true];
const FEED_COLORS = ["#22D3EE", "#FB923C", "#34D399", "#F87171"];
const PORTAL_COLORS = ["#FB923C", "#34D399", "#22D3EE"];
const FINANCE_COLORS = ["#34D399", "#FBBF24", "#22D3EE"];
const HR_COLORS = ["#34D399", "#34D399", "#FBBF24", "#22D3EE"];

/* ---------- PULSE ---------- */
function PulseVisual({ v }) {
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <span className="relative flex h-2.5 w-2.5">
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full bg-[#34D399]"
              animate={{ scale: [1, 2.4, 1], opacity: [0.8, 0, 0.8] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#34D399]" />
          </span>
          {v.live}
        </div>
        <span className="text-[11px] text-white/40">{v.now}</span>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {v.stats.map((s, i) => (
          <div key={s.l} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            <div className="text-[11px] text-white/50">{s.l}</div>
            <div className="mt-1 flex items-end justify-between">
              <span className="font-heading text-2xl font-extrabold text-white">{s.v}</span>
              <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${PULSE_UP[i] ? "text-[#34D399]" : "text-[#FB923C]"}`}>
                {PULSE_UP[i] ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />} {s.d}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {v.feed.map((f, i) => (
          <motion.div
            key={f}
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.12 }}
            className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-3 py-2.5"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: FEED_COLORS[i] }} />
            <span className="text-xs text-white/70">{f}</span>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------- INTELLIGENCE ---------- */
function IntelligenceVisual({ v }) {
  const bars = [40, 62, 55, 78, 68, 90, 84];
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-white">{v.title}</div>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#34D399]/15 px-2 py-0.5 text-[11px] font-semibold text-[#34D399]">
          <TrendingUp className="h-3 w-3" /> +23%
        </span>
      </div>
      <div className="mt-6 flex h-40 items-end justify-between gap-2.5">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: "easeOut" }}
            className="flex-1 rounded-t-lg bg-gradient-to-t from-[#F97316]/40 to-[#FB923C]"
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-white/35">
        {v.days.map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="mt-5 rounded-2xl border border-[#22D3EE]/25 bg-[#22D3EE]/[0.06] p-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#22D3EE]">
          <Activity className="h-3.5 w-3.5" /> {v.aiTitle}
        </div>
        <p className="mt-1.5 text-xs text-white/70 leading-relaxed">{v.aiText}</p>
      </div>
    </Panel>
  );
}

/* ---------- PORTAL ---------- */
function PortalVisual({ v }) {
  return (
    <Panel>
      <div className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#F87171]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FBBF24]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#34D399]" />
        <div className="ml-3 flex-1 rounded-md bg-white/5 px-3 py-1 text-[10px] text-white/40">{v.url}</div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <div className="text-sm font-semibold text-white">{v.heading}</div>
        <span className="rounded-lg bg-gradient-to-r from-[#F97316] to-[#FB923C] px-3 py-1.5 text-[11px] font-semibold text-white">{v.newRequest}</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {v.rows.map((r, i) => (
          <motion.div
            key={r.t}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 + i * 0.12 }}
            className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3"
          >
            <span className="text-xs text-white/75">{r.t}</span>
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold" style={{ background: `${PORTAL_COLORS[i]}22`, color: PORTAL_COLORS[i] }}>{r.s}</span>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------- FINANCE ---------- */
function FinanceVisual({ v }) {
  return (
    <Panel>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
          <div className="text-[11px] text-white/50">{v.revenueLabel}</div>
          <div className="mt-1 font-heading text-2xl font-extrabold text-white">{v.revenueValue}</div>
          <div className="mt-1 text-[11px] font-semibold text-[#34D399]">{v.revenueNote}</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
          <div className="text-[11px] text-white/50">{v.collectionLabel}</div>
          <div className="mt-1 font-heading text-2xl font-extrabold text-white">{v.collectionValue}</div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              initial={{ width: 0 }} whileInView={{ width: "92%" }} viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#FB923C]"
            />
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {v.rows.map((r, i) => (
          <motion.div
            key={r.n}
            initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.25 + i * 0.1 }}
            className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3"
          >
            <span className="text-xs text-white/70">{r.n}</span>
            <div className="flex items-center gap-3">
              <span className="font-heading text-sm font-bold text-white">{r.a}</span>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${FINANCE_COLORS[i]}22`, color: FINANCE_COLORS[i] }}>{r.s}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------- HR ---------- */
function HrVisual({ v }) {
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-white">{v.title}</div>
        <span className="inline-flex items-center gap-1 text-[11px] text-white/45"><Clock className="h-3 w-3" /> {v.today}</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {v.stats.map((s) => (
          <div key={s.l} className="rounded-2xl border border-white/8 bg-white/[0.03] p-3 text-center">
            <div className="font-heading text-xl font-extrabold text-white">{s.v}</div>
            <div className="text-[10px] text-white/50">{s.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {v.team.map((m, i) => (
          <motion.div
            key={m.n}
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.25 + i * 0.1 }}
            className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-2.5"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#F97316]/30 to-[#22D3EE]/20 text-[11px] font-bold text-white">
                {m.n.split(" ").map((x) => x[0]).join("")}
              </span>
              <div>
                <div className="text-xs font-semibold text-white">{m.n}</div>
                <div className="text-[10px] text-white/45">{m.r}</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-[11px] text-white/55">{m.in}</span>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${HR_COLORS[i]}22`, color: HR_COLORS[i] }}>{m.st}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
}

export default function TasFlowStory() {
  const { lang } = useLang();
  const s = TASFLOW_I18N[lang].story;
  const v = TASFLOW_I18N[lang].storyVisuals;

  return (
    <div className="relative overflow-hidden" data-testid="tasflow-story">
      <div className="pointer-events-none absolute left-1/4 top-40 h-[420px] w-[420px] rounded-full bg-[#F97316]/10 blur-[150px]" />
      <div className="pointer-events-none absolute right-1/4 bottom-40 h-[380px] w-[380px] rounded-full bg-[#22D3EE]/10 blur-[150px]" />

      <StoryRow id="pulse" testid="tasflow-story-pulse" icon={Activity} {...s.pulse}>
        <PulseVisual v={v.pulse} />
      </StoryRow>

      <StoryRow id="intelligence" testid="tasflow-story-intelligence" icon={BarChart3} reverse {...s.intelligence}>
        <IntelligenceVisual v={v.intelligence} />
      </StoryRow>

      <StoryRow id="portal" testid="tasflow-story-portal" icon={LayoutDashboard} {...s.portal}>
        <PortalVisual v={v.portal} />
      </StoryRow>

      <StoryRow id="finans" testid="tasflow-story-finance" icon={Wallet} reverse {...s.finance}>
        <FinanceVisual v={v.finance} />
      </StoryRow>

      <StoryRow id="ik" testid="tasflow-story-hr" icon={Users} {...s.hr}>
        <HrVisual v={v.hr} />
      </StoryRow>
    </div>
  );
}
